import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  HomeIcon as HomeOutlineIcon,
  HomeIcon as HomeSolidIcon,
  HeartIcon as HeartOutlineIcon,
  HeartIcon as HeartSolidIcon,
  ShoppingCartIcon as CartOutlineIcon,
  ShoppingCartIcon as CartSolidIcon,
  MagnifyingGlassIcon as SearchOutlineIcon,
  MagnifyingGlassIcon as SearchSolidIcon,
} from "@heroicons/react/24/outline";
import { useCart } from "../../../context/CartContext";
import { useWishlist } from "../../../context/WishlistContext";
import "./BottomNav.css";

const BottomNav = () => {
  const location = useLocation();
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const handleModalOpen = () => setIsVisible(false);
    const handleModalClose = () => setIsVisible(true);

    // Listen for modal open/close events
    window.addEventListener('modalOpen', handleModalOpen);
    window.addEventListener('modalClose', handleModalClose);

    // Cleanup
    return () => {
      window.removeEventListener('modalOpen', handleModalOpen);
      window.removeEventListener('modalClose', handleModalClose);
    };
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const navItems = useMemo(() => [
    { 
      icon: (active) => active ? 
        <HomeSolidIcon className="w-6 h-6" /> : 
        <HomeOutlineIcon className="w-6 h-6" />,
      path: "/",
      label: "Home"
    },
    { 
      icon: (active) => active ? 
        <SearchSolidIcon className="w-6 h-6" /> : 
        <SearchOutlineIcon className="w-6 h-6" />,
      path: "/searchEngine",
      label: "Search"
    },
    { 
      icon: (active) => active ? 
        <HeartSolidIcon className="w-6 h-6" /> : 
        <HeartOutlineIcon className="w-6 h-6" />,
      path: "/wishlist",
      label: "Wishlist",
      count: wishlistCount,
      showBadge: true
    },
    { 
      icon: (active) => active ? 
        <CartSolidIcon className="w-6 h-6" /> : 
        <CartOutlineIcon className="w-6 h-6" />,
      path: "/cart",
      label: "Cart",
      count: cartCount,
      showBadge: true
    }
  ], [wishlistCount, cartCount]);

  // Debug: Log when component renders
  useEffect(() => {
    console.log('BottomNav rendered with items:', navItems);
  }, [navItems]);

  if (!isVisible) return null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200">
      <div className="flex justify-around items-center h-16">
        {navItems.map((item) => {
          const isItemActive = isActive(item.path);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`relative flex flex-col items-center justify-center flex-1 h-full ${
                isItemActive ? 'text-[#1F509A]' : 'text-gray-600'
              }`}
            >
              <div className="relative">
                {item.icon(isItemActive)}
                {item.showBadge && item.count > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                    {item.count > 99 ? '99+' : item.count}
                  </span>
                )}
              </div>
              <span className="text-xs mt-1">{item.label}</span>
              {isItemActive && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-1 bg-[#1F509A] rounded-full" />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
